import SaveIcon from '@mui/icons-material/Save'
import { Box, Divider, Fab, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useRef, useState } from 'react'
import { CabinetSizesType } from '../../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../../common/Helpers'
import { CabinetSize } from '../Category/CategoryUIBox'

interface IProps {
    cabinetSizes: CabinetSizesType

    editCabinetSizes(name: string, value: string, enqueueSnackbar: EnqueueSnackbarType): void
}


const CabinetSizes: FC<IProps> = ({ cabinetSizes, ...props }) => {

    const [sizeProps, setSizeProps] = useState({
        cabinetType: 'base',
        userType: 'retail',
        widthValues: cabinetSizes.base_retail_cabinet_sizes.width.values,
        heightValues: cabinetSizes.base_retail_cabinet_sizes.height.values,
        depthValues: cabinetSizes.base_retail_cabinet_sizes.depth.values,
        widthDefault: cabinetSizes.base_retail_cabinet_sizes.width.default,
        heightDefault: cabinetSizes.base_retail_cabinet_sizes.height.default,
        depthDefault: cabinetSizes.base_retail_cabinet_sizes.depth.default,
    })
    const sizePropsRef = useRef(sizeProps)

    const [needSave, setNeedSave] = useState(false)
    const needSaveRef = useRef(needSave)

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        const needSave = JSON.stringify({
            width: { default: sizeProps.widthDefault, values: sizeProps.widthValues },
            height: {
                default: sizeProps.heightDefault,
                values: sizeProps.heightValues
            },
            depth: {
                default: sizeProps.depthDefault,
                values: sizeProps.depthValues
            }
            // @ts-ignore
        }) !== JSON.stringify(cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'])

        setNeedSave(needSave)

        sizePropsRef.current = sizeProps
        needSaveRef.current = needSave
    })

    useEffect(() => {
        setAllValues()
    }, [cabinetSizes, sizeProps.userType, sizeProps.cabinetType])


    useEffect(() => () => {
        if (needSaveRef.current) {
            if (window?.confirm('Save changes?')) {
                onSaveClick()
            }
        }
    }, [])

    const setAllValues = () => {
        setSizeProps({
            // @ts-ignore
            ...sizeProps, widthValues: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].width.values,
            // @ts-ignore
            heightValues: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].height.values,
            // @ts-ignore
            depthValues: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].depth.values,
            // @ts-ignore
            widthDefault: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].width.default,
            // @ts-ignore
            heightDefault: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].height.default,
            // @ts-ignore
            depthDefault: cabinetSizes[sizeProps.cabinetType + '_' + sizeProps.userType + '_cabinet_sizes'].depth.default
        })
    }

    const onSaveClick = () => {

        const cabinetType = sizePropsRef.current.cabinetType
        const userType = sizePropsRef.current.userType
        const widthValues = sizePropsRef.current.widthValues
        const heightValues = sizePropsRef.current.heightValues
        const depthValues = sizePropsRef.current.depthValues
        const widthDefault = sizePropsRef.current.widthDefault
        const heightDefault = sizePropsRef.current.heightDefault
        const depthDefault = sizePropsRef.current.depthDefault

        if (widthValues.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of width')
            return
        }
        if (heightValues.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of height')
            return
        }
        if (depthValues.length === 0) {
            showMessage(enqueueSnackbar, 'Write at least one value of depth')
            return
        }
        if (!widthValues.includes(widthDefault)) {
            showMessage(enqueueSnackbar, 'Choose default value for width')
            return
        }
        if (!heightValues.includes(heightDefault)) {
            showMessage(enqueueSnackbar, 'Choose default value for height')
            return
        }
        if (!depthValues.includes(depthDefault)) {
            showMessage(enqueueSnackbar, 'Choose default value for depth')
            return
        }

        needSaveRef.current = false
        setNeedSave(false)

        props.editCabinetSizes(
            cabinetType + '_' + userType + '_cabinet_sizes',
            JSON.stringify({
                width: { default: widthDefault, values: widthValues },
                height: {
                    default: heightDefault,
                    values: heightValues
                },
                depth: {
                    default: depthDefault,
                    values: depthValues
                }
            }),
            enqueueSnackbar
        )
    }

    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            pb={2}
            style={{
                width: 400,
                height: 'fit-content',
                margin: 15,
                border: '1px solid black',
                maxHeight: '90%',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
            }}
        >
            <Box
                py={1}
                style={{
                    position: 'sticky',
                    top: 0,
                    width: '100%',
                    backgroundColor: 'white',
                    zIndex: 10
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={3}> </Grid>
                    <Grid item xs={6} container justifyContent='center' alignItems='center'>
                        <Typography variant='h6'>Edit Cabinet Sizes</Typography>
                    </Grid>
                    <Grid item xs={3} container justifyContent='flex-end' alignItems='center'>
                        <Tooltip title='Save'>
                            <Fab
                                color={needSave ? 'secondary' : "primary"}
                                size='small'
                                onClick={onSaveClick}
                                style={{ marginRight: 10 }}
                            >
                                <SaveIcon />
                            </Fab>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>


            <FormControl component="fieldset">
                <RadioGroup row
                    value={sizeProps.cabinetType}
                    onChange={e => {
                        if (needSave) {
                            if (window?.confirm('Save changes?')) {
                                onSaveClick()
                            }
                        }

                        setSizeProps({ ...sizeProps, cabinetType: e.target.value })
                    }}
                >
                    <FormControlLabel value="base" control={<Radio color='primary' />} label="Base" />
                    <FormControlLabel value="tall" control={<Radio color='primary' />} label="Tall" />
                    <FormControlLabel value="upper" control={<Radio color='primary' />} label="Upper" />
                </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
                <RadioGroup row
                    value={sizeProps.userType}
                    onChange={e => {
                        if (needSave) {
                            if (window?.confirm('Save changes?')) {
                                onSaveClick()
                            }
                        }

                        setSizeProps({ ...sizeProps, userType: e.target.value })
                    }}
                >
                    <FormControlLabel value="retail" control={<Radio color='primary' />} label="Retail" />
                    <FormControlLabel value="dealer" control={<Radio color='primary' />} label="Dealer" />
                    <FormControlLabel value="designer" control={<Radio color='primary' />} label="Designer" />
                </RadioGroup>
            </FormControl>

            <CabinetSize
                name='Width' values={sizeProps.widthValues} defaultValue={sizeProps.widthDefault}
                setValues={(v: number[]) => { setSizeProps({ ...sizeProps, widthValues: v }) }}
                setDefaultValue={(v: number) => { setSizeProps({ ...sizeProps, widthDefault: v }) }}
            />
            <CabinetSize
                name='Height' values={sizeProps.heightValues} defaultValue={sizeProps.heightDefault}
                setValues={(v: number[]) => { setSizeProps({ ...sizeProps, heightValues: v }) }}
                setDefaultValue={(v: number) => { setSizeProps({ ...sizeProps, heightDefault: v }) }}
            />
            <CabinetSize
                name='Depth' values={sizeProps.depthValues} defaultValue={sizeProps.depthDefault}
                setValues={(v: number[]) => { setSizeProps({ ...sizeProps, depthValues: v }) }}
                setDefaultValue={(v: number) => { setSizeProps({ ...sizeProps, depthDefault: v }) }}
            />

            <Divider style={{ width: '80%', marginTop: 15, marginBottom: 5 }} />

        </Box>
    )
}

export default CabinetSizes